import { t } from '../../i18next';
import { MakeOrBuyInputType, makeOrBuyRules } from './businessLogic';
import { getPoKubuMapping } from './services/poKubu';
import { isMobDecisionMob } from './services/mobDecision';
import { validateSheet1HtzService } from './services/validateSheet1HtzService';

type ResultType = {
  isValid: boolean;
  errors: { [key: string]: { field: string; msg: string } };
};

function hasValue(value: unknown) {
  return !(value === null || value === undefined || value === '');
}

function isWholeNumber(value: unknown) {
  if (!hasValue(value)) {
    return true;
  }

  if (typeof value === 'string') {
    return /^\d+$/.test(value.trim());
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) && Number.isInteger(numericValue) && numericValue >= 0;
}

export async function extraValidator(props): Promise<ResultType> {
  const { row, rows, tableDetails } = props;
  const result: ResultType = {
    isValid: true,
    errors: {},
  };
  try {
    const filteredRow = rows.filter((item) => {
      return !item.status;
    });
    const newCheck = filteredRow.findIndex((item) => item['npi_htz'] == row['npi_htz']);
    if (newCheck >= 0) {
      result.isValid = false;
      result.errors['npi_htz'] = {
        field: 'HTZ',
        msg: `The following HTZ number already exists: ${row['npi_htz']}.`,
      };
    }

    if (!isWholeNumber(row.speedline_qty)) {
      result.isValid = false;
      result.errors.speedline_qty = {
        field: 'Speedline QTY',
        msg: t('mob.speedline_qty_whole_numbers'),
      };
    }

    // QSFA is mandatory once a MoB decision is set to any value other than "MoB".
    const mobNowSet = hasValue(row?.npi_mob);
    const mobIsMob = isMobDecisionMob(row?.npi_mob);
    const qsfaEmpty = !hasValue(row?.qsfaneed);
    if (mobNowSet && !mobIsMob && qsfaEmpty) {
      result.isValid = false;
      result.errors['qsfaneed'] = {
        field: 'QSFA needed',
        msg: t('mob.qsfa_needed_mandatory'),
      };
      return result;
    }

    const poKubuMapping = await getPoKubuMapping(tableDetails.id);
    const {
      bestellnummer: bestellNummer,
      debitor_name: debitorName,
      design_owner_id: designOwnerId,
      htz_type_id: htzTypeId,
      khk_pkey: khkPkey,
      lumpsum_due: lumpSumDue,
      npi_htz: npiHtz,
      npi_mob: makeOrBuy,
      novelty_id: noveltyId,
      npi_accept_po: npiAcceptPO,
      npi_as_kenner: npiAsKenner,
      npi_comment: npiComment,
      nrp_due: nrpDue,
      pag_deliverydate_due: pagDeliveryDateDue,
      pag_followup_date: pagFollowUpDate,
      pag_ispartof: pagIsPartOf,
      po_kubu_id: poKubuId,
      pre_htz: preHtz,
      pre_htz_manufactured_by: preHtzManufacturedBy,
      project_id: projectId,
      rp_due: rpDue,
    } = row;
    const poKubu = poKubuMapping.find((element) => element.id === poKubuId)?.po_kubu || null;
    const sheet1Htz = await validateSheet1HtzService(npiHtz, tableDetails.id);
    const decisionTreeInput: MakeOrBuyInputType = {
      bestellNummer,
      debitorName,
      designOwnerId,
      htzTypeId,
      khkPkey,
      lumpSumDue,
      makeOrBuy,
      makeOrBuyModified: row.backup?.npi_mob !== makeOrBuy,
      noveltyId,
      npiAcceptPO,
      npiAsKenner,
      npiComment,
      nrpDue,
      pagDeliveryDateDue,
      pagFollowUpDate,
      pagIsPartOf,
      poKubu,
      preHtz,
      preHtzManufacturedBy,
      projectId,
      rpDue,
      sheet1Htz,
    };
    const decisionTreeResult = hasValue(makeOrBuy)
      ? makeOrBuyRules(decisionTreeInput)
      : { save: true };
    if (decisionTreeResult?.error) {
      result.errors.npi_htz = { field: npiHtz, msg: decisionTreeResult.error };
      result.isValid = false;
    }

    return result;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return {
      isValid: false,
      errors: {
        npi_htz: {
          field: row.npi_htz,
          msg: errorMessage,
        },
      },
    };
  }
}

export function uploadValidator({ row, mandatoryColumns }) {
  const result = { isValid: true, message: '' };
  const givenColumns = Object.keys(row);
  const missingColumns = mandatoryColumns.filter(
    (column: string) => !givenColumns.includes(column),
  );
  result.isValid = missingColumns.length <= 0;
  result.message = result.isValid
    ? ''
    : t('mob.attribute_missing') + missingColumns.join(', ') + '.';
  return result;
}
